<template>
  <h2 class="p-3">Diagnosis rates with CKD screening</h2>
  <div class="legenddiv" ref="legenddiv"></div>
  <div class="chartdiv" ref="chartdiv"></div>
  <div class="p-3">
    <p><small>CKD, chronic kidney disease; RASi, renin–angiotensin system inhibitor.</small></p>
    <p><small>Tip: hover the cursor over the graph to show data labels. Click the graph legend to show/hide a bar.</small></p>
  </div>
</template>

<script>
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";

export default {
  name: "DiagnosisRatesWithCkdScreening",
  props: {
    country: String,
  },
  async mounted() {

    // Add license key to remove logo
    am5.addLicense("AM5C317022592");

    let root = am5.Root.new(this.$refs.chartdiv);
    var legendRoot = am5.Root.new(this.$refs.legenddiv);

    // Set themes
    // https://www.amcharts.com/docs/v5/concepts/themes/
    root.setThemes([
      am5themes_Animated.new(root)
    ]);

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/xy-chart/
    var chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: "panX",
      // wheelY: "zoomX",
      layout: root.verticalLayout
    }));

    // Data
    let response = await fetch(`/data/diagnosis-rates-with-ckd-screening/${this.country}.json`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    const json = await response.json();

    let data = [];

    json.forEach((element) => {
      data.push({
        age: `${element.age}`,
        currentPractice: element.currentPractice,
        screeningTreated: element.screeningTreated,
        screeningUntreated: element.screeningUntreated,
      });
    });

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    var xRenderer = am5xy.AxisRendererX.new(root, {
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
      strokeWidth: 1,
      strokeOpacity: 1
    })

    var xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "age",
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(root, {})
    }));

    xRenderer.grid.template.setAll({
      location: 1
    });

    xAxis.data.setAll(data);

    var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      numberFormat: "###",
      min: 0,
      renderer: am5xy.AxisRendererY.new(root, {
        strokeOpacity: 1,
        strokeWidth: 1
      })
    }));

    var yRenderer = yAxis.get("renderer");
    yRenderer.ticks.template.setAll({
      stroke: am5.color(0x000000),
      visible: true
    });

    yAxis.get("renderer").grid.template.set("forceHidden", true);
    xAxis.get("renderer").grid.template.set("forceHidden", true);


    yAxis.children.unshift(
      am5.Label.new(root, {
        rotation: 270,
        text: "CKD diagnosis rate (per 100 000 people screened)",
        fontWeight: 'bold',
        fontSize: 16,
        x: am5.p50,
        centerX: am5.percent(50),
        y: am5.p50,
        centerY: am5.percent(50),
        paddingBottom: 20,
        paddingTop: 0
      })
    );

    // Add series
    // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
    function makeSeries(name, fieldName, color, type) {
      var series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: name,
        fill: color,
        xAxis: xAxis,
        yAxis: yAxis,
        type: type,
        valueYField: fieldName,
        categoryXField: "age"
      }));

      // Format number so it shows two decimal places even with a zero on the end
      root.numberFormatter.setAll({
        numberFormat: "####.",
        numericFields: ["valueY"]
      });

      series.columns.template.setAll({
        tooltipText: "{valueY}",
        width: am5.percent(90),
        tooltipY: 0,
        strokeOpacity: 0
      });

      legend.data.push(series);
      series.data.setAll(data);

      // series.appear();

      series.bullets.push(function() {
        return am5.Bullet.new(root, {
          locationY: 0,
          sprite: am5.Label.new(root, {
            text: "{valueY}",
            fill: root.interfaceColors.get("alternativeText"),
            centerY: 0,
            centerX: am5.p50,
            populateText: true
          }),
        });
      });
    }

    // Add legend
    // https://www.amcharts.com/docs/v5/charts/xy-chart/legend-xy-series/
    var legend = legendRoot.container.children.unshift(
      am5.Legend.new(legendRoot, {
        nameField: "name",
        fillField: "color",
        strokeField: "color",
        centerX: am5.percent(50),
        x: am5.percent(56),
        layout: root.verticalLayout
      }),
    );
    legend.markers.template.setAll({
      width: 15,
      height: 15
    });

    legend.labels.template.setAll({
      fontSize: 14,
      fontWeight: "300"
    });

    // Chart series
    makeSeries("Diagnosed via current practice", "currentPractice", "#143762");
    makeSeries("Diagnosed via screening, treated with RASi", "screeningTreated", "#ffc107");
    makeSeries("Diagnosed via screening, untreated", "screeningUntreated", "#3F4444");


    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    chart.appear(1000, 100);
    },

}// end am5.ready()
</script>

<style scoped>

.chartdiv {
  width: 100%;
  height: 500px;
}

.legenddiv {
  width: 100%;
  height: 100px;
}
</style>
